'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  type Transition,
} from 'framer-motion';
import { ResumeBuilderPreviewCard, CARD_WIDTH_PX } from './ResumeBuilderPreviewCard';
import { RESUME_SHOWCASE_SLIDES } from './sampleResumes';

/** How long the front card sits before the deck shuffles. */
const ROTATE_INTERVAL_MS = 4500;

/** How many cards deep the fan shows at once — kept small on purpose (see below). */
const VISIBLE_DEPTH = 3;

const TRANSITION: Transition = { duration: 0.6, ease: [0.22, 1, 0.36, 1] };

/** Resting pose for each depth in the fan, front (0) to back. */
const STACK_POSE = [
  { x: 0, y: 0, scale: 1, rotate: 2, opacity: 1 },
  { x: 16, y: 14, scale: 0.95, rotate: 6, opacity: 0.8 },
  { x: 30, y: 26, scale: 0.9, rotate: 10, opacity: 0.5 },
] as const;

/** Where the front card animates to as it's dealt away. */
const EXIT_POSE = { x: -70, y: -6, scale: 0.85, rotate: -6, opacity: 0 };

/** Where a newly-revealed card animates in from, one step behind the back slot. */
const ENTER_POSE = { x: 42, y: 38, scale: 0.86, rotate: 13, opacity: 0 };

/** Fraction of scroll delta the stage drifts by — a gentle lag, not a full parallax. */
const SCROLL_PARALLAX_FACTOR = 0.1;

/** Max hover-tilt rotation, in degrees, applied to the front card only. */
const MAX_TILT_DEG = 5;

/** Spring the hover tilt follows the cursor with, and eases back to flat on mouse leave. */
const TILT_SPRING = { stiffness: 300, damping: 30, mass: 0.5 };

/**
 * Hero illustration for /resume-builder: a stack of real resumes — different
 * people, different templates — that auto-shuffles like a deck of cards.
 *
 * Performance/perf note: only `VISIBLE_DEPTH` (3) cards are ever mounted, so
 * at most 3 preview iframes exist at once (briefly 4 while one is mid-exit).
 * This works by keeping a `frontIndex` into the full slide list and, each
 * render, taking a 3-wide window `[frontIndex, frontIndex+1, frontIndex+2]`
 * (mod length). Advancing `frontIndex` drops the old front slide out of the
 * window (AnimatePresence plays its exit — slide left, scale down, fade) and
 * brings the next slide in the deck into the newly-vacated back slot
 * (mounting fresh, animating up from ENTER_POSE). The two slides in between
 * simply get new target poses and glide forward — they're never unmounted,
 * so their iframes aren't re-created. Each slide keeps a stable `id` as its
 * React key regardless of which depth it currently occupies, which is what
 * lets framer-motion tell "moved" apart from "entered"/"left".
 *
 * Two decorative effects layer on top, both skipped under
 * `prefers-reduced-motion` and both kept out of React state so they never
 * trigger a re-render of the card stack (and its iframes) on every
 * mousemove/scroll tick:
 *  - Hover tilt: the front card leans toward the cursor. `onMouseMove`
 *    computes an offset from the stage's center and writes it straight into
 *    framer-motion `MotionValue`s (`.set()`, no `setState`); a `useSpring`
 *    on top smooths the follow and glides back to flat on mouse leave.
 *  - Scroll parallax: the whole stage drifts up slightly slower than the
 *    page scrolls, via a rAF-throttled scroll listener that writes directly
 *    to `stageRef.current.style.transform`.
 */
export function ResumeCarousel() {
  const slides = RESUME_SHOWCASE_SLIDES;
  const total = slides.length;
  const prefersReducedMotion = useReducedMotion();
  const [frontIndex, setFrontIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const stageRef = useRef<HTMLDivElement>(null);

  // Raw cursor-driven tilt values (degrees), updated imperatively in
  // handleMouseMove below. The springs smooth the follow and are what
  // actually get read by the front card's `style` — see the JSX.
  const tiltXRaw = useMotionValue(0);
  const tiltYRaw = useMotionValue(0);
  const tiltX = useSpring(tiltXRaw, TILT_SPRING);
  const tiltY = useSpring(tiltYRaw, TILT_SPRING);

  useEffect(() => {
    if (prefersReducedMotion || isPaused || total <= 1) return;
    const id = setInterval(() => {
      setFrontIndex((current) => (current + 1) % total);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [prefersReducedMotion, isPaused, total]);

  // Scroll parallax (see class doc above). Mutates the DOM directly instead
  // of going through React state/props so scrolling never re-renders the
  // stack. Re-runs if prefers-reduced-motion changes live, and its cleanup
  // clears any transform already applied — so toggling the OS setting mid
  // session can't leave the stage stuck off-position.
  useEffect(() => {
    if (prefersReducedMotion) return;
    const stage = stageRef.current;
    if (!stage) return;

    // globals.css caps <html> to the viewport height and gives <body>
    // `overflow-y: auto`, so BODY is what actually scrolls on this page —
    // a plain `window`/`document.documentElement` scroll listener never
    // fires. Same quirk worked around in
    // app/landing/lib/useLandingRailEffects.ts; resolve whichever element
    // actually has scrollable overflow, falling back to window.
    const docEl = document.scrollingElement as HTMLElement | null;
    const scroller: HTMLElement | Window =
      docEl && docEl.scrollHeight > window.innerHeight + 1
        ? docEl
        : document.body.scrollHeight > window.innerHeight + 1
          ? document.body
          : window;
    const scrollTopOf = () =>
      scroller === window ? window.scrollY : (scroller as HTMLElement).scrollTop;

    let frameId = 0;
    const applyParallax = () => {
      frameId = 0;
      stage.style.transform = `translateY(${scrollTopOf() * SCROLL_PARALLAX_FACTOR}px)`;
    };
    const onScroll = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(applyParallax);
    };

    applyParallax();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      if (frameId) cancelAnimationFrame(frameId);
      stage.style.transform = '';
    };
  }, [prefersReducedMotion]);

  const visibleSlides = useMemo(() => {
    // Reduced-motion users get the original single static card — no timer,
    // no stack, matching how this hero looked before the carousel existed.
    const depth = prefersReducedMotion ? 1 : Math.min(VISIBLE_DEPTH, total);
    return Array.from({ length: depth }, (_, depthIndex) => ({
      ...slides[(frontIndex + depthIndex) % total],
      depth: depthIndex,
    }));
  }, [frontIndex, prefersReducedMotion, slides, total]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const offsetX = (event.clientX - bounds.left) / bounds.width - 0.5; // -0.5 (left edge) .. 0.5 (right edge)
    const offsetY = (event.clientY - bounds.top) / bounds.height - 0.5; // -0.5 (top edge) .. 0.5 (bottom edge)
    tiltYRaw.set(offsetX * 2 * MAX_TILT_DEG);
    tiltXRaw.set(offsetY * -2 * MAX_TILT_DEG);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    tiltXRaw.set(0);
    tiltYRaw.set(0);
  };

  return (
    // Shifted right once the hero becomes a two-column grid (see page.tsx),
    // so the stack extends past the grid's right edge instead of sitting
    // flush with it — a deliberate "breaking out of the container" effect.
    // Kept on its own wrapper rather than folded into stageRef's own inline
    // style: the scroll-parallax effect above writes a whole new `transform`
    // value straight to stageRef.current.style, which would otherwise
    // clobber a translateX set there via Tailwind/inline style.
    <div className="lg:translate-x-12">
      <div
        ref={stageRef}
        aria-hidden="true"
        className="relative mx-auto w-full lg:mx-0"
        style={{ maxWidth: CARD_WIDTH_PX, aspectRatio: '210 / 297' }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={handleMouseLeave}
        onMouseMove={prefersReducedMotion ? undefined : handleMouseMove}
      >
        <AnimatePresence initial={false}>
          {visibleSlides.map((slide) => (
            <motion.div
              key={slide.id}
              className="absolute inset-0"
              style={
                slide.depth === 0 && !prefersReducedMotion
                  ? { zIndex: VISIBLE_DEPTH, rotateX: tiltX, rotateY: tiltY, transformPerspective: 800 }
                  : { zIndex: VISIBLE_DEPTH - slide.depth }
              }
              initial={slide.depth === VISIBLE_DEPTH - 1 ? ENTER_POSE : false}
              animate={STACK_POSE[slide.depth]}
              exit={EXIT_POSE}
              transition={TRANSITION}
            >
              <ResumeBuilderPreviewCard content={slide.content} settings={slide.settings} className="h-full w-full" />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
